import React, { useEffect, useMemo, useRef } from "react";
import Accordion from "components/common/Accordian";
import useCartNotes from "lib/hooks/cartNotes";
import { useLocale } from "lib/hooks/useLocale";
import { CartNoteIcon } from "lib/types/cartNotes";
import { currencyFormatter } from "lib/utils/formatters";
import { classNames, truncateString } from "lib/utils/helpers";
import { Edit, Edit2, Gift, Paperclip, Trash2, AlertCircle } from "react-feather";
import { errorToast } from "lib/utils/toasters";

const getCartNoteIcon = (icon: CartNoteIcon) => {
  switch (icon) {
    case "ATTACH":
      return <Paperclip size={16} className="min-w-4 min-h-4" />;
    case "EDIT":
      return <Edit size={16} className="min-w-4 min-h-4" />;
    case "GIFT":
      return <Gift size={16} className="min-w-4 min-h-4" />;
    default:
      return null;
  }
};

const CartNotes = () => {
  const cartNoteRef = useRef<any>(null);
  const { t } = useLocale();
  const {
    state: { cartNoteAttributes, appliedCartNote, isCartNoteTileLoading, isEditMode, inputValue, showError },
    actions: { handleSaveNote, setIsEditMode, setInputValue, setShowError },
  } = useCartNotes();

  useEffect(() => {
    if (isEditMode) {
      cartNoteRef?.current?.openAccordion();
    }
  }, [isEditMode]);

  const handleCancel = () => {
    if (isEditMode) {
      setIsEditMode(false);
      setInputValue(appliedCartNote.note);
    } else {
      setInputValue("");
    }
    setShowError(false)
    cartNoteRef?.current?.closeAccordion();
  };

  const handleSave = async (action: "ADD" | "EDIT" | "DELETE" = "ADD") => {
    if (!inputValue.trim()) {
      setShowError(true);
      return;
    }
    try {
      handleSaveNote({ inputValue, action });
    } catch (error) {
      console.error("Error saving note");
      errorToast(t("error_saving_note"));
    }
  };

  const handleEdit = () => setIsEditMode(true);

  const handleDelete = () => handleSaveNote({ inputValue: "", action: "DELETE" });

  const showCartNoteTile = useMemo(() => {
    if (isEditMode) {
      return true;
    } else if (!appliedCartNote.hasCartNoteAdded) {
      return true;
    }
    return false;
  }, [appliedCartNote.hasCartNoteAdded, isEditMode]);

  if (!cartNoteAttributes.hasCartNote) {
    return <></>;
  }

  const renderAppliedCartNote = () => {
    return (
      <div className="flex flex-col gap-2 p-4 border border-gray-light rounded-xl" onClick={handleEdit}>
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            {getCartNoteIcon(cartNoteAttributes.cartNoteConfig.icon)}
            <p className="text-sm text-coal-light text-ellipsis ">
              {truncateString(appliedCartNote.note, 50)}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleEdit}
              className="text-coal-dark hover:underline text-sm flex items-center gap-1">
              <Edit2 size={18} />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-500 hover:underline text-sm flex items-center gap-1">
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const cartNoteSkeleton = () => {
    return (
      <div className="border-base rounded-md h-[70px] w-full px-2 py-3 space-y-2">
        <div className="skeleton-loader h-[16px] rounded-md w-2/5"></div>
        <div className="skeleton-loader h-[14px] rounded-md w-full"></div>
      </div>
    );
  };

  const renderCartNoteTile = () => {
    return (
      <Accordion
        id={"cart_notes"}
        ref={cartNoteRef}
        defaultOpen={false}
        titleIcon={<div className=" text-base"></div>}
        titleText={
          <div className=" text-base flex gap-2 items-center">
            {getCartNoteIcon(cartNoteAttributes.cartNoteConfig.icon)}
            <h3 className="text-sm font-medium">{cartNoteAttributes.cartNoteConfig.title}</h3>
            {cartNoteAttributes.type === "PAID" && (
              <p className="text-xs text-gray-dark">
                ({currencyFormatter(cartNoteAttributes?.cartNoteAddOnData?.price?.toString())})
              </p>
            )}
          </div>
        }
        callbackOnToggle={(isOpen: boolean) => {
          setIsEditMode(false);
        }}
        buttonClass="!h-auto !py-3 !rounded-lg"
        customClass="!mx-0 !mt-0"
        accordionIcon="PLUS"
        content={
          <div className="flex flex-col px-3 pb-3 gap-2">
            <div className="flex w-full flex-col gap-2">
              <textarea
                className={classNames(
                  "resize-none p-2 border-[1px] border-gray-light rounded-lg text-sm scrollbar-hide",
                  showError ? "!border-2 border-ouch" : ""
                )}
                rows={5}
                maxLength={200}
                name=""
                id=""
                value={inputValue}
                onChange={(e) => {
                  setShowError(false);
                  setInputValue(e.target.value);
                }}
                placeholder={cartNoteAttributes.cartNoteConfig.suggestionText}></textarea>
              {showError && (
                <p className="text-sm text-ouch flex flex-row items-center gap-1">
                  <AlertCircle size={14} />
                  {t("cart_note_error")}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button className="p-2 text-carbon rounded-lg px-3 text-sm" onClick={handleCancel}>
                {t("cancel")}
              </button>
              <button
                className="bg-primary-dark p-2 text-white rounded-lg px-3 text-sm"
                onClick={() => handleSave("ADD")}>
                {t("save")}
              </button>
            </div>
          </div>
        }
      />
    );
  };

  return (
    <div>
      {appliedCartNote.hasCartNoteAdded && !isEditMode && !isCartNoteTileLoading && renderAppliedCartNote()}
      {isCartNoteTileLoading && cartNoteSkeleton()}
      {showCartNoteTile && !isCartNoteTileLoading && renderCartNoteTile()}
    </div>
  );
};

export default CartNotes;
